import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
  requireBillingAccess?: boolean;
}

/** The only screens a נציג טלפוני may open, matched EXACTLY.
 *
 *  The customer page is not one of them: an agent identifies callers through
 *  the dialer, which shows a name and number, and never sees the full record.
 *  Exact matching also keeps /calls/report — the org-wide telephony report —
 *  out, which a startsWith('/calls') prefix would have admitted. */
const AGENT_ROUTES = ['/calls', '/profile'];

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireSuperAdmin = false, requireBillingAccess = false }) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, isSuperAdmin, hasBillingAccess, isAgent } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        טוען...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // A נציג טלפוני reaches the dialer and a customer card, nothing else. Login
  // sends everyone to /dashboard, and every dashboard endpoint is 403 for this
  // role — so without this the first screen after logging in is empty, with no
  // nav to escape from. Redirecting here covers every route at once.
  const agentPath = AGENT_ROUTES.includes(location.pathname);
  if (isAgent && !agentPath) {
    return <Navigate to="/calls" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireBillingAccess && !hasBillingAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;